import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { getInvoice, reconcileInvoice, updateInvoiceFields } from 'Services/invoices';
import { trackError } from 'Utils/errorMonitoring';
import { INVOICES_RECONCILED_STATUS } from 'Utils/constants';

const isExpired = (date) => {
    if (!date) return false;

    return new Date().getTime() > date.getTime();
};

const formatInvoiceData = (data) => {
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (key === 'date_payment') {
                return [key, value ? new Date(value) : null];
            }
            return [key, value];
        }),
    );
};

export default function useInvoice(invoiceId, options = {}) {
    const {
        data: invoice,
        isLoading,
        refetch,
        error,
    } = useQuery({
        queryKey: ['invoices', invoiceId],
        queryFn: () => getInvoice(invoiceId),
        ...options,
        onError: (error) => {
            if (error.response?.status === 404) return;

            trackError(error);
        },
    });

    const queryClient = useQueryClient();

    const { mutate: reconcile, isLoading: isReconciling } = useMutation({
        mutationFn: async ({ transactionsIds, forceReconciliationStatus = null }) =>
            await reconcileInvoice(invoiceId, { transactionsIds, forceReconciliationStatus }),
        onMutate: async ({ transactionsIds, forceReconciliationStatus }) => {
            await queryClient.cancelQueries({ queryKey: ['invoices'] });
            const previousInvoice = queryClient.getQueryData(['invoices', invoiceId]);

            const getReconciledStatus = (currentStatus) => {
                if (forceReconciliationStatus === true) return INVOICES_RECONCILED_STATUS.RECONCILED;
                if (forceReconciliationStatus === false) {
                    return isExpired(previousInvoice?.date_expiration)
                        ? INVOICES_RECONCILED_STATUS.NOT_RECONCILED_LATE
                        : INVOICES_RECONCILED_STATUS.NOT_RECONCILED;
                }
                return currentStatus;
            };

            queryClient.setQueryData(['invoices', invoiceId], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    transactions_associated: transactionsIds,
                    reconciled_status: getReconciledStatus(old.reconciled_status),
                };
            });

            return { previousInvoice };
        },
        onError: (err, _variables, context) => {
            trackError(err);
            if (context.previousInvoice) {
                queryClient.setQueryData(['invoices', invoiceId], context.previousInvoice);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateInvoiceMutate, isLoading: isUpdatingFields } = useMutation(
        (data) => updateInvoiceFields(invoiceId, data),
        {
            onMutate: (data) => {
                const previousInvoice = queryClient.getQueryData(['invoices', invoiceId]);

                const dataFormatted = formatInvoiceData(data);
                queryClient.setQueryData(['invoices', invoiceId], (old) => {
                    if (!old) return old;

                    return {
                        ...old,
                        ...dataFormatted,
                    };
                });

                return { previousInvoice };
            },
            onError: (err, _module, context) => {
                trackError(err);
                queryClient.setQueryData(['invoices', invoiceId], context.previousInvoice);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['invoices'] });
                queryClient.invalidateQueries({ queryKey: ['dashboard'] });
                queryClient.invalidateQueries({ queryKey: ['forecasts'] });
            },
        },
    );

    const isUpdating = isReconciling || isUpdatingFields;

    return {
        invoice,
        isLoading,
        error,
        refetch,
        reconcile,
        isReconciling,
        updateInvoice: updateInvoiceMutate,
        isUpdating,
    };
}
